/**
 * «Organisasjoner vi samarbeider med» (sak 239.21, migrering 0541).
 *
 * Atle 04.10: organisasjonen legger til en samarbeidspartner selv, og den andre godtar eller avslår.
 * Samme to rader som byråkoblingen (`organization_links`), én i hvert hus; `direction` sier hvem som
 * spurte. En godtatt samarbeidspartner ser salene som står på «Hus vi samarbeider med».
 *
 * Cellene: `organization_partner:create` (legge til, trekke, avslutte) og `:respond` (godta, avslå).
 * Lista leses med `organization:read`.
 */
import { z } from 'zod';

import { ORGANIZATION_LINK_STATUSES } from './organization-links.js';

export const ORGANIZATION_PARTNER_DIRECTIONS = ['outgoing', 'incoming'] as const;
export type OrganizationPartnerDirection = (typeof ORGANIZATION_PARTNER_DIRECTIONS)[number];

/** Typen på en organisasjon man kan samarbeide med: aldri personlige hus eller plattformen. */
export const ORGANIZATION_PARTNER_KINDS = ['festival', 'agency'] as const;

export const organizationPartnerSchema = z
  .object({
    /** Vår rad i koblingen — det handlingene adresserer. */
    id: z.uuid(),
    /** Den andre organisasjonen. */
    organizationId: z.uuid(),
    name: z.string(),
    kind: z.enum(ORGANIZATION_PARTNER_KINDS).nullable(),
    status: z.enum(ORGANIZATION_LINK_STATUSES),
    /**
     * `outgoing`: vi spurte. `incoming`: de spurte oss, og svaret er vårt. Null for en byråkobling
     * fra e-postlenken (0084) — den vises som samarbeid når den er godtatt, men avsluttes ikke her.
     */
    direction: z.enum(ORGANIZATION_PARTNER_DIRECTIONS).nullable(),
    createdAt: z.iso.datetime(),
    acceptedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'OrganizationPartner' });
export type OrganizationPartner = z.infer<typeof organizationPartnerSchema>;

/** Ikke paget: en organisasjon har en håndfull samarbeidspartnere. */
export const organizationPartnerListResponseSchema = z
  .object({ items: z.array(organizationPartnerSchema) })
  .meta({ id: 'OrganizationPartnerListResponse' });
export type OrganizationPartnerListResponse = z.infer<typeof organizationPartnerListResponseSchema>;

/** Navn og type — aldri mer om en organisasjon man ikke samarbeider med. */
export const organizationPartnerCandidateSchema = z
  .object({
    organizationId: z.uuid(),
    name: z.string(),
    kind: z.enum(ORGANIZATION_PARTNER_KINDS),
  })
  .meta({ id: 'OrganizationPartnerCandidate' });
export type OrganizationPartnerCandidate = z.infer<typeof organizationPartnerCandidateSchema>;

export const organizationPartnerCandidatesQuerySchema = z
  .object({ search: z.string().trim().max(100).optional() })
  .meta({ id: 'OrganizationPartnerCandidatesQuery' });
export type OrganizationPartnerCandidatesQuery = z.infer<
  typeof organizationPartnerCandidatesQuerySchema
>;

/** Høyst 20 treff: velgeren søker, den blar ikke. */
export const organizationPartnerCandidatesResponseSchema = z
  .object({ items: z.array(organizationPartnerCandidateSchema) })
  .meta({ id: 'OrganizationPartnerCandidatesResponse' });
export type OrganizationPartnerCandidatesResponse = z.infer<
  typeof organizationPartnerCandidatesResponseSchema
>;

export const requestOrganizationPartnerRequestSchema = z
  .object({ organizationId: z.uuid() })
  .meta({ id: 'RequestOrganizationPartnerRequest' });
export type RequestOrganizationPartnerRequest = z.infer<
  typeof requestOrganizationPartnerRequestSchema
>;
